import { useCallback, useState, useMemo } from 'react';
import { Material, MaterialSortKey } from '../types';
import * as materialsService from '../services/materials.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseMaterialsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
}

export function useMaterials({ accessToken, refreshToken, attemptRefresh }: UseMaterialsProps) {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState<{ status: 'all' | 'active' | 'inactive'; search: string }>({
    status: 'all',
    search: ''
  });

  // Sorting
  const [materialsSort, setMaterialsSort] = useState<{
    column: MaterialSortKey;
    direction: 'asc' | 'desc';
  }>({
    column: 'code',
    direction: 'asc'
  });

  // Dialog state
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [materialCode, setMaterialCode] = useState('');
  const [materialName, setMaterialName] = useState('');
  const [materialDensity, setMaterialDensity] = useState('');
  const [materialPricePerKg, setMaterialPricePerKg] = useState('');
  const [materialActive, setMaterialActive] = useState(true);
  const [materialAllThicknesses, setMaterialAllThicknesses] = useState(true);
  const [materialThicknessInput, setMaterialThicknessInput] = useState('');
  const [materialDimensionLimits, setMaterialDimensionLimits] = useState('');
  const [materialSaving, setMaterialSaving] = useState(false);

  const fetchMaterials = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setLoading(true);

    try {
      const items = await withAuthRetry(
        () => materialsService.fetchMaterials(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setMaterials(items);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar dados'
      };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const createMaterial = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setMaterialSaving(true);

    try {
      const availableThicknesses = materialAllThicknesses
        ? []
        : materialThicknessInput
            .split(',')
            .map((t) => parseFloat(t.trim()))
            .filter((t) => !isNaN(t) && t > 0);

      await withAuthRetry(
        () => materialsService.createMaterial(
          {
            code: materialCode,
            name: materialName,
            density: parseFloat(materialDensity),
            pricePerKg: parseFloat(materialPricePerKg),
            active: materialActive,
            availableThicknesses,
            dimensionLimits: materialDimensionLimits || undefined
          },
          accessToken!
        ),
        refreshToken,
        attemptRefresh
      );

      // Clear form
      setMaterialDialogOpen(false);
      setSelectedMaterial(null);
      setMaterialCode('');
      setMaterialName('');
      setMaterialDensity('');
      setMaterialPricePerKg('');
      setMaterialActive(true);
      setMaterialAllThicknesses(true);
      setMaterialThicknessInput('');
      setMaterialDimensionLimits('');

      await fetchMaterials();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao criar material'
      };
    } finally {
      setMaterialSaving(false);
    }
  }, [
    accessToken,
    refreshToken,
    attemptRefresh,
    materialCode,
    materialName,
    materialDensity,
    materialPricePerKg,
    materialActive,
    materialAllThicknesses,
    materialThicknessInput,
    materialDimensionLimits,
    fetchMaterials
  ]);

  const updateMaterial = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !selectedMaterial) return { success: false, error: 'Não autenticado' };

    setMaterialSaving(true);

    try {
      const availableThicknesses = materialAllThicknesses
        ? []
        : materialThicknessInput
            .split(',')
            .map((t) => parseFloat(t.trim()))
            .filter((t) => !isNaN(t) && t > 0);

      await withAuthRetry(
        () => materialsService.updateMaterial(
          {
            id: selectedMaterial.id,
            code: materialCode,
            name: materialName,
            density: parseFloat(materialDensity),
            pricePerKg: parseFloat(materialPricePerKg),
            active: materialActive,
            availableThicknesses,
            dimensionLimits: materialDimensionLimits || undefined
          },
          accessToken!
        ),
        refreshToken,
        attemptRefresh
      );

      // Clear form
      setMaterialDialogOpen(false);
      setSelectedMaterial(null);
      setMaterialCode('');
      setMaterialName('');
      setMaterialDensity('');
      setMaterialPricePerKg('');
      setMaterialActive(true);
      setMaterialAllThicknesses(true);
      setMaterialThicknessInput('');
      setMaterialDimensionLimits('');

      await fetchMaterials();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao atualizar material'
      };
    } finally {
      setMaterialSaving(false);
    }
  }, [
    accessToken,
    refreshToken,
    attemptRefresh,
    selectedMaterial,
    materialCode,
    materialName,
    materialDensity,
    materialPricePerKg,
    materialActive,
    materialAllThicknesses,
    materialThicknessInput,
    materialDimensionLimits,
    fetchMaterials
  ]);

  const toggleMaterialsSort = useCallback((column: MaterialSortKey) => {
    setMaterialsSort((current) =>
      current.column === column
        ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  }, []);

  const toggleMaterialStatus = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    try {
      await withAuthRetry(
        () => materialsService.toggleMaterialStatus(id, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchMaterials();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao alterar status do material'
      };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchMaterials]);

  const openMaterialDialog = useCallback((material?: Material) => {
    if (material) {
      setSelectedMaterial(material);
      setMaterialCode(material.code);
      setMaterialName(material.name);
      setMaterialDensity(material.density.toString());
      setMaterialPricePerKg(material.pricePerKg.toString());
      setMaterialActive(material.active);

      if (!material.availableThicknesses || material.availableThicknesses.length === 0) {
        setMaterialAllThicknesses(true);
        setMaterialThicknessInput('');
      } else {
        setMaterialAllThicknesses(false);
        setMaterialThicknessInput(material.availableThicknesses.join(', '));
      }

      setMaterialDimensionLimits(material.dimensionLimits || '');
    } else {
      setSelectedMaterial(null);
      setMaterialCode('');
      setMaterialName('');
      setMaterialDensity('');
      setMaterialPricePerKg('');
      setMaterialActive(true);
      setMaterialAllThicknesses(true);
      setMaterialThicknessInput('');
      setMaterialDimensionLimits('');
    }
    setMaterialDialogOpen(true);
  }, []);

  const getMaterialStatusRank = (material: Material) => {
    if (material.deletedAt) return 0;
    if (!material.active) return 1;
    return 2;
  };

  const sortedMaterials = useMemo(() => {
    const byStatus = materials.filter((item) => {
      if (filters.status === 'active') return item.active && !item.deletedAt;
      if (filters.status === 'inactive') return !item.active && !item.deletedAt;
      return true;
    });

    const bySearch = byStatus.filter(
      (item) =>
        !filters.search ||
        item.code.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.name.toLowerCase().includes(filters.search.toLowerCase())
    );

    const direction = materialsSort.direction === 'asc' ? 1 : -1;

    return bySearch.sort((a, b) => {
      if (materialsSort.column === 'status') {
        return (getMaterialStatusRank(a) - getMaterialStatusRank(b)) * direction;
      }
      if (materialsSort.column === 'updatedAt') {
        return (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) * direction;
      }
      const aVal = a[materialsSort.column];
      const bVal = b[materialsSort.column];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }, [materials, filters, materialsSort]);

  return {
    // State
    materials,
    sortedMaterials,
    loading,
    filters,
    setFilters,
    materialsSort,

    // Dialog state
    materialDialogOpen,
    setMaterialDialogOpen,
    selectedMaterial,
    materialCode,
    setMaterialCode,
    materialName,
    setMaterialName,
    materialDensity,
    setMaterialDensity,
    materialPricePerKg,
    setMaterialPricePerKg,
    materialActive,
    setMaterialActive,
    materialAllThicknesses,
    setMaterialAllThicknesses,
    materialThicknessInput,
    setMaterialThicknessInput,
    materialDimensionLimits,
    setMaterialDimensionLimits,
    materialSaving,

    // Actions
    fetchMaterials,
    createMaterial,
    updateMaterial,
    toggleMaterialsSort,
    toggleMaterialStatus,
    openMaterialDialog
  };
}
